import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { deleteThemeVersion, getThemeVersionConfig } from '../../functions/API';
import { useScene } from '../../hooks';
import { useEditor } from '../../hooks/useEditor';
import { LauncherVersionData } from '../../types/hooks';
import { TextButton } from '../generic/TextButton';
import { Dialog } from '../generic/dialog/Dialog';

export const Versions = ({
  setDialogOpen,
}: {
  setDialogOpen: (state: boolean) => void;
}) => {
  const { launcherThemeSession, setLauncherThemeSession } = useEditor();
  const scene = useScene();
  const versions = launcherThemeSession.versions;

  const [IDsBeingRemoved, setIDsBeingRemoved] = useState<string[]>([]);

  const handleDeleteVersion = async (version: LauncherVersionData) => {
    if (!launcherThemeSession.themeID) return;

    setIDsBeingRemoved((prev) => [...prev, version.id]);

    await toast.promise(
      deleteThemeVersion({
        themeID: launcherThemeSession.themeID,
        versionID: version.id,
      }),
      {
        pending: 'Deleting the theme version',
        success: 'The theme version has been deleted',
        error: 'Error while deleting the theme version',
      }
    );

    setLauncherThemeSession((themeData) => ({
      ...themeData,
      versions: themeData.versions.filter((item) => item.id !== version.id),
    }));

    setIDsBeingRemoved((prev) => prev.filter((item) => item !== version.id));
  };

  return versions ? (
    versions.length === 0 ? (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        There are no saved versions yet
      </div>
    ) : (
      <ScrollArea>
        {versions.map((version) => {
          return (
            <Item
              key={version.id}
              $disabled={IDsBeingRemoved.includes(version.id)}
            >
              <div>
                <Dialog
                  title="Delete theme version"
                  description="Are you sure you want to delete the theme version?"
                  triggerElement={
                    <div className="removeButton">
                      <i className="icon-not-visible" />
                    </div>
                  }
                  cancelElement={<TextButton>Cancel</TextButton>}
                  actionElement={
                    <TextButton
                      handleClick={() => handleDeleteVersion(version)}
                      type="error"
                    >
                      Yes, delete
                    </TextButton>
                  }
                >
                  <div style={{ pointerEvents: 'none' }}>
                    <Item>
                      <div>
                        <span className="versionCreateTime">
                          {convertToReadableDate(version.createdAt)}
                        </span>
                        <span className="versionID">{version.id}</span>
                      </div>
                      <img src={version.thumbnailUrl || ''} />
                    </Item>
                  </div>
                </Dialog>

                <span className="versionCreateTime">
                  {convertToReadableDate(version.createdAt)}
                </span>
                <span className="versionID">{version.id}</span>
              </div>
              <img
                loading="lazy"
                src={version.thumbnailUrl || ''}
                onClick={async () => {
                  if (!launcherThemeSession.themeID) return;

                  setDialogOpen(false);

                  const versionConfig = await toast.promise(
                    getThemeVersionConfig({
                      themeID: launcherThemeSession.themeID,
                      versionID: version.id,
                    }),
                    {
                      pending: 'Loading the theme version',
                      success: 'The theme version has been applied',
                      error: 'Error while loading the theme version',
                    }
                  );

                  scene.importLauncherConfigJSON(versionConfig);
                }}
              />
            </Item>
          );
        })}
      </ScrollArea>
    )
  ) : (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
      }}
    >
      Loading...
    </div>
  );
};

const convertToReadableDate = (timeString: string) => {
  const date = new Date(timeString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const ScrollArea = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-height: 400px;
  padding: 0 10px;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--scrollbar-track-color);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb-color);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const Item = styled.div<{
  $disabled?: boolean;
}>`
  position: relative;
  user-select: none;
  box-sizing: border-box;
  height: 150px;
  margin: 5px 0;
  padding: 5px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border: 1px solid var(--panel-bg-color-hover);
  border-radius: 10px;

  transition: background-color 0.3s ease;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  & > img {
    cursor: pointer;
    max-height: 100%;
    max-width: 50%;
  }

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .versionCreateTime {
      font-size: 13px;
      color: var(--text-color);
    }

    .versionID {
      user-select: text;
      font-size: 10px;
      color: var(--text-color-secondary);
    }
  }

  .removeButton {
    cursor: pointer;
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 5px;
    border-radius: 5px;

    font-size: 12px;
    background-color: var(--panel-bg-color-secondary);

    &:hover {
      background-color: var(--panel-bg-color);
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    `
      pointer-events: none;
      opacity: 0.5;
  `}
`;
